import {
  EVENT_FILTER_VALUES,
  EventLimit,
  FilterableEvent,
  ManualReviewEventFilter,
  MaterialAreaFilter,
  matchesAnyEventFilter,
  parseEventLimit,
  ReviewReasonFilter,
} from "components/manualReview/filters/eventFilters";
import { Setter } from "hooks/state/syncing";
import { literalListSerialiser } from "hooks/serialisers/literalList";
import { materialAreaListSerialiser } from "hooks/serialisers/materialAreaList";
import { reviewReasonListSerialiser } from "hooks/serialisers/reviewReasonList";
import { Serialiser } from "hooks/serialisers/types";
import { useQueryParamState } from "hooks/useQueryParamState";
import { createContext, ReactNode, use, useCallback, useMemo } from "react";

interface EventFilterContextValue {
  filters: ManualReviewEventFilter[];
  setFilters: Setter<ManualReviewEventFilter[]>;
  consumptionLimitTo: EventLimit | null;
  setConsumptionLimitTo: Setter<EventLimit | null>;
  materialAreaFilters: MaterialAreaFilter[];
  setMaterialAreaFilters: Setter<MaterialAreaFilter[]>;
  reviewReasonFilters: ReviewReasonFilter[];
  setReviewReasonFilters: Setter<ReviewReasonFilter[]>;
  matches: (event: FilterableEvent) => boolean;
}

const NOOP_VALUE: EventFilterContextValue = {
  filters: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setFilters: () => {},
  consumptionLimitTo: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setConsumptionLimitTo: () => {},
  materialAreaFilters: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setMaterialAreaFilters: () => {},
  reviewReasonFilters: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setReviewReasonFilters: () => {},
  matches: () => true,
};

const EventFilterContext = createContext<EventFilterContextValue | null>(null);

const eventFilterSerialiser = literalListSerialiser(EVENT_FILTER_VALUES);
const consumptionLimitToSerialiser: Serialiser<EventLimit | null, never> = {
  format: (value) => value ?? "",
  parse: (text) => ({ valid: true, value: parseEventLimit(text) }),
};

const EMPTY_FILTERS: ManualReviewEventFilter[] = [];
const EMPTY_MATERIAL_AREA_FILTERS: MaterialAreaFilter[] = [];
const EMPTY_REVIEW_REASON_FILTERS: ReviewReasonFilter[] = [];

export const useEventFilters = (): EventFilterContextValue => {
  const context = use(EventFilterContext);
  return context ?? NOOP_VALUE;
};

export const useEventFilterMatcher = (): ((
  event: FilterableEvent
) => boolean) => useEventFilters().matches;

export const EventFilterProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useQueryParamState<
    ManualReviewEventFilter[],
    never
  >("eventFilters", eventFilterSerialiser, EMPTY_FILTERS);
  const [consumptionLimitTo, setConsumptionLimitTo] = useQueryParamState<
    EventLimit | null,
    never
  >("consumptionLimitTo", consumptionLimitToSerialiser, null);
  const [materialAreaFilters, setMaterialAreaFilters] = useQueryParamState<
    MaterialAreaFilter[],
    never
  >(
    "materialAreaFilters",
    materialAreaListSerialiser,
    EMPTY_MATERIAL_AREA_FILTERS
  );
  const [reviewReasonFilters, setReviewReasonFilters] = useQueryParamState<
    ReviewReasonFilter[],
    never
  >(
    "reviewReasonFilters",
    reviewReasonListSerialiser,
    EMPTY_REVIEW_REASON_FILTERS
  );

  const matches = useCallback(
    (event: FilterableEvent) =>
      matchesAnyEventFilter(
        event,
        filters,
        consumptionLimitTo,
        materialAreaFilters,
        reviewReasonFilters
      ),
    [filters, consumptionLimitTo, materialAreaFilters, reviewReasonFilters]
  );

  const value = useMemo(
    () => ({
      filters,
      setFilters,
      consumptionLimitTo,
      setConsumptionLimitTo,
      materialAreaFilters,
      setMaterialAreaFilters,
      reviewReasonFilters,
      setReviewReasonFilters,
      matches,
    }),
    [
      filters,
      setFilters,
      consumptionLimitTo,
      setConsumptionLimitTo,
      materialAreaFilters,
      setMaterialAreaFilters,
      reviewReasonFilters,
      setReviewReasonFilters,
      matches,
    ]
  );

  return <EventFilterContext value={value}>{children}</EventFilterContext>;
};
